import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonNestedTable from '@/components/common/commonTable/CommonNestedTable';
import { TableColumn } from '@/components/common/commonTable/CommonTable';
import ExportOption from '@/components/common/export/ExportOption';
import ImagePreview from '@/components/common/media/ImagePreview';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import { useListQueryParams } from '@/hooks/useListQueryParams';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import {
  ICategoryData,
  ICategoryResponse,
} from '@/interfaces/categoryResponse.interface';
import { ActionConfig, PageInfo } from '@/interfaces/common.interface';
import {
  filterOptions,
  GetFilterUrl,
  getStatusWarning,
  showToastNotification,
  statusOptions,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface CategoryStates {
  isLoading: boolean;
  categories: ICategoryData[];
  pageInfo: PageInfo;
  selectedCategories: Set<string | number>;
  showDeleteModel: boolean;
  selectedCategoryId: string | null;
  showUpdateModel: boolean;
  statusToUpdate: string | null;
}

const Categories = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePermissions();

  const { params, setParams } = useListQueryParams({
    search: '',
    categoryStatus: '',
    orderDir: 'newest',
  });

  const { page, limit, search, categoryStatus, orderDir } = params;

  const [states, setStates] = useState<CategoryStates>({
    isLoading: false,
    categories: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit,
      page,
    },
    selectedCategories: new Set(),
    showDeleteModel: false,
    selectedCategoryId: null,
    showUpdateModel: false,
    statusToUpdate: null,
  });

  const handleChangeState = (payload: Partial<CategoryStates>) => {
    setStates((prev) => ({ ...prev, ...payload }));
  };

  const fetchCategories = async () => {
    try {
      handleChangeState({ isLoading: true });

      const query = GetFilterUrl({
        page,
        limit,
        status: categoryStatus,
        search,
        sort: orderDir,
      });

      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}?${query}`;
      const res = await callApi<ICategoryResponse>(PATH, { method: 'GET' });

      handleChangeState({
        categories: res.data,
        pageInfo: res.pageInfo,
        isLoading: false,
      });
    } catch {
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [page, limit, search, categoryStatus, orderDir]);

  // 🔹 Update status
  const onStatusUpdate = async () => {
    if (!states.selectedCategoryId || !states.statusToUpdate) return;

    try {
      handleChangeState({ isLoading: true });

      await callApi(
        `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}/${states.selectedCategoryId}`,
        {
          method: 'PATCH',
          data: { status: states.statusToUpdate },
        },
      );

      fetchCategories();
    } finally {
      handleChangeState({
        showUpdateModel: false,
        selectedCategoryId: null,
        statusToUpdate: null,
        isLoading: false,
      });
    }
  };

  // 🔹 Delete category
  const handleDeleteCategory = async () => {
    if (!states.selectedCategoryId) return;

    try {
      handleChangeState({ isLoading: true });

      await callApi(
        `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}/${states.selectedCategoryId}`,
        { method: 'DELETE' },
      );

      showToastNotification('success', 'Category deleted successfully');
      setParams({ page: 1 });
    } finally {
      handleChangeState({
        showDeleteModel: false,
        selectedCategoryId: null,
        isLoading: false,
      });
    }
  };

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.categories);

  // 🔹 Columns
  const columns: TableColumn<ICategoryData>[] = [
    {
      key: 'name',
      header: 'Category',
      render: (item) => (
        <ImagePreview
          src={`${API_URL_PRODUCT_SERVICE}/${item?.media?.[0]?.url}`}
          text={item.name}
        />
      ),
    },
    {
      key: 'productCount',
      header: 'Products',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.status}
          options={statusOptions}
          canEditVal="category"
          onChange={(status) =>
            handleChangeState({
              showUpdateModel: true,
              selectedCategoryId: item.id,
              statusToUpdate: status,
            })
          }
        />
      ),
    },
  ];

  const childColumns: TableColumn<ICategoryData>[] = [
    {
      key: 'name',
      header: 'Sub Category',
      render: (item) => (
        <ImagePreview
          src={`${API_URL_PRODUCT_SERVICE}/${item?.media?.[0]?.url}`}
          text={item.name}
        />
      ),
    },
    {
      key: 'colorCode',
      header: 'Color Code',
      render: (i) => (
        <span
          style={{ backgroundColor: i.colorCode }}
          className="p-2 rounded-md"
        >
          {i.colorCode}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.status}
          options={statusOptions}
          canEditVal="category"
          onChange={(status) =>
            callApi(
              `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}/${item.id}`,
              { method: 'PATCH', data: { status } },
            ).then(fetchCategories)
          }
        />
      ),
    },
  ];

  const categoryActionConfig: Record<string, ActionConfig<ICategoryData>> = {
    edit: {
      text: 'Edit',
      onClick: (category) =>
        navigate(`${RoutesLink.addCategory}/${category.id}`),
      permission: () => canEdit('category'),
    },
    delete: {
      text: 'Delete',
      onClick: (category) =>
        handleChangeState({
          showDeleteModel: true,
          selectedCategoryId: category.id,
        }),
      permission: () => canDelete('category'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(categoryActionConfig)
        .filter((a) => a.permission())
        .map(({ permission, ...rest }) => rest),
    [canEdit, canDelete],
  );

  const sortOptions = [
    { value: 'az', label: 'A To Z' },
    { value: 'za', label: 'Z To A' },
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
  ];

  return (
    <Layout>
      <PageHeading text="Categories" />

      <Card>
        <div className="flex justify-between items-start gap-3">
          <CommonFilter
            activeFilter={categoryStatus}
            filters={filterOptions}
            inputPlaceHolder="Search for Categories..."
            searchValue={search}
            onSearch={(text) => setParams({ search: text })}
            onChange={(filter) =>
              setParams({ categoryStatus: filter, page: 1 })
            }
            filterOptions={{
              values: sortOptions,
              onChange: (value) => setParams({ orderDir: value }),
            }}
          />

          {states.categories.length > 0 && (
            <ExportOption
              APIPath={`${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}/export/excel`}
              status={categoryStatus}
            />
          )}
        </div>

        <CommonNestedTable
          columns={columns}
          data={states.categories}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No Categories Found"
          loading={states.isLoading}
          actions={actions}
          getChildren={(item) => item.subcategories}
          childColumns={childColumns}
        />

        <Pagination
          paginationData={states.pageInfo}
          onPageChange={(p) => setParams({ page: p })}
          onPageSizeChange={(l) => setParams({ limit: l })}
          isLoading={states.isLoading}
        />
      </Card>

      {states.showDeleteModel && (
        <ConfirmationModel
          type="category"
          show
          onClose={() =>
            handleChangeState({
              showDeleteModel: false,
              selectedCategoryId: null,
            })
          }
          onConfirm={handleDeleteCategory}
        />
      )}

      {states.showUpdateModel && (
        <ConfirmationModel
          modelType="update"
          show
          warning={
            states.statusToUpdate === 'inactive'
              ? getStatusWarning('category')
              : ''
          }
          subTitle={`This action will update the status to ${states.statusToUpdate}`}
          onClose={() =>
            handleChangeState({
              showUpdateModel: false,
              selectedCategoryId: null,
              statusToUpdate: null,
            })
          }
          onConfirm={onStatusUpdate}
        />
      )}
    </Layout>
  );
};

export default Categories;
